<template>
  <div class="modal" ref="modalRef" data-bs-backdrop="static" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <form class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            <img src="@/assets/icons/mail_gray_60.png"/>
            Group invitation
          </h3>
          <button type="button" class="btn-close" :disabled="busyRef" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div v-if="inviteRef" class="modal-body">
          <div class="invite-header">
            <template v-if="!isNewRef">
              <label class="form-label">From</label>
              <div>{{ senderUserRef?.displayName }}</div>
            </template>
            <label class="form-label">To</label>
            <div v-if="recipientUserRef">
              {{ recipientUserRef?.displayName }}
              <span v-if="recipientUserRef.email" class="address">({{ recipientUserRef.email }})</span>
            </div>
            <div v-else-if="isNewRef">
              {{ recipientProfileRef?.displayName }}
            </div>
            <div v-else>
              {{ recipientProfileRef?.displayName }}
              <span v-if="recipientEmailRef" class="address">({{ recipientEmailRef }})</span>
            </div>
            <template v-if="isNewRef && !recipientUserRef">
              <label class="form-label">Address</label>
              <div class="address-input">
                <FamilyGroupInviteRecipient
                  ref="recipientInputRef"
                  :profile-id="recipientProfileRef?.id"/>
                <ContactProviderSelector/>
              </div>
            </template>
            <template v-if="!isNewRef">
              <label class="form-label">Sent</label>
              <div class="invite-date">
                {{ DateTimeUtil.toUserActionDate(inviteRef.lastSentDate) }}
                ({{ DateTimeUtil.toRelativePastDate(inviteRef.lastSentDate) }})
              </div>
            </template>
          </div>
          <div v-if="showResentAlertRef" class="alert alert-success alert-sm resent-alert">
            Invitation resent.
          </div>
          <div v-if="isAcceptedRef" class="alert alert-success alert-sm">
            This invitation was accepted on
            {{ DateTimeUtil.toUserActionDate(inviteRef?.closedDate) }}
          </div>
          <div v-if="inviteRef.result == InvitationResult.Cancelled" class="alert alert-warning alert-sm cancelled-alert">
            This invitation was cancelled on {{ DateTimeUtil.toUserActionDate(inviteRef?.closedDate) }}.
          </div>
          <div class="invite-body">
            <div class="invite-group">
              <label class="form-label">
                Join this group:
              </label>
              <FamilyGroupItem :family-group-id="inviteRef.familyGroupId"/>
            </div>
            <div v-if="showInviteProfileRef" class="invite-profile">
              <label class="form-label">
                Join as this person:
              </label>
              <ProfileCard v-if="isNewRef" :person-id="recipientProfileRef?.id" class="profile-card"/>
              <FamilyGroupInviteProfile v-else :invite-id="inviteRef?.id" class="profile-card"/>
            </div>
            <div v-if="isRecruiterRef || canSetRecruiterRef" class="invite-role">
              <label class="form-label">
                <input type="checkbox" v-model="isRecruiterRef" :disabled="!isNewRef" class="form-check-input">
                Can invite others
              </label>
              <div v-if="isNewRef || enableResendRef" class="message">
                <label class="form-label">
                  <input type="checkbox" v-model="addMessageRef" class="form-check-input">
                  Add a personal message <span class="help-text">(recommended)</span>
                </label>
                <div id="customMessage" ref="customMessageElemRef" class="collapse" :class="{ show: addMessageRef }">
                  <textarea v-model="inviteRef.senderMessage" class="form-control"></textarea>
                </div>
              </div>
              <div v-else-if="inviteRef.senderMessage" class="message">
                <label class="form-label">Message</label>
                <div>{{ inviteRef.senderMessage }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" v-if="showCancelRef"
            class="btn btn-outline-danger secondary-action"
            :disabled="!enableCancelRef"
            @click="cancelInvite">
            <template v-if="busyRef"><Spinner/> Cancelling...</template>
            <template v-else>Cancel invitation</template>
          </button>
          <button type="submit" v-if="showSendRef"
            class="btn btn-primary"
            :disabled="!enableSendRef"
            @click.prevent="sendInvite">
            <template v-if="busyRef"><Spinner/> Sending...</template>
            <template v-else><SendIcon/> Send</template>
          </button>
          <button type="button" v-if="showResendRef"
            class="btn btn-outline-primary"
            :disabled="!enableResendRef"
            @click="resendInvite">
            <template v-if="busyRef"><Spinner/> Resending...</template>
            <template v-else><SendIcon/> Resend</template>
          </button>
          <button type="button" v-if="canAcceptRef"
            class="btn btn-primary"
            :disabled="busyRef"
            @click="acceptInvite">
            <template v-if="busyRef"><Spinner/> Accepting...</template>
            <template v-else>Accept</template>
          </button>
          <button type="button" v-if="!showSendRef"
            class="btn"
            :class="{ 
              ['btn-primary']: !canAcceptRef,
              ['btn-outline-primary']: canAcceptRef,
            }" 
            :disabled="busyRef" 
            data-bs-dismiss="modal">
            Close
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-title {
  position: relative;
  padding-left: 4rem;

  img {
    position: absolute;
    width: 3rem;
    height: 3rem;
    top: 0;
    left: 0;
    filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
  }
}

.modal-body {
  padding: 0.5rem 1rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    padding: 0.5rem 2rem 2rem;
  }

  .alert {
    margin: 0;
  }
 
  .invite-header {
    border-bottom: 1px solid #ddd;
    padding-bottom: 1rem;
    display: grid;
    grid-template-columns: max-content 1fr;
    align-items: baseline;
    column-gap: 1rem;
    row-gap: 0.25rem;

    .address {
      font-size: 0.875rem;
      color: #888;
    }

    .address-input {
      display: flex;    
      align-items: baseline;
      gap: 0.5rem;

      .invite-recipient {
        flex-grow: 1;
      }

      .use-contacts {
        align-self: center;
      }
    }

    .invite-date {
      font-size: 0.875rem;
      color: #888;
    }
  }

  .invite-body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0 0.5rem;

    .form-label {
      margin-bottom: 0.25rem;
      font-size: 1rem;
      color: #333;
    }

    input[type="checkbox"] {
      margin-right: 0.25rem;
    }

    .family-group-item {
      margin-left: 0.5rem;
      font-size: 0.875rem;
    }

    .profile-card {
      margin-top: 0.5rem;
      margin-left: 0.5rem;
    }

    .message textarea {
      min-height: 5rem;
      font-size: 0.875rem;
    }
  }
}

.modal-footer {
  button {
    min-width: 100px;
  }
  
  .secondary-action {
    margin-left: 1rem;
    margin-right: auto;
  }
}
</style>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue'
import { DateTimeUtil } from '@/util/LuxonUtil'
import { FamilyGroupInvite, GroupMemberRole, InvitationResult, ItemPermissions, UserGender } from '@/gp/GroupAdminModel'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useFamilyGroupInviteStore } from '@/gp/FamilyGroupInviteStore'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useUserStore } from '@/gp/UserStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useManageStore } from "@/manage/ManageStores"
import { useContactStore } from "@/manage/contacts/ContactStore"
import { LoadMode } from '@/util/AsyncData'
import { AsyncUtil } from '@/util/AsyncUtil'
import { CompositeId } from '@/rd/CompositeId'
import { useTokenManager } from '@/auth/TokenManager'
import { Modal as BsModal } from 'bootstrap'
import { SendIcon } from '@zhuowenli/vue-feather-icons'
import { DateTime } from 'luxon'
import { PersonName } from '@/rd/ResearchDataModel'
import FamilyGroupInviteRecipient from './FamilyGroupInviteRecipient.vue'
import ContactProviderSelector from '@/manage/contacts/ContactProviderSelector.vue'
import FamilyGroupItem from './FamilyGroupItem.vue'
import FamilyGroupInviteProfile from './FamilyGroupInviteProfile.vue'
import ProfileCard from '@/manage/ProfileCard.vue'
import Spinner from '@/util/Spinner.vue'

defineExpose({
  openNew,
  openEdit,
  close,
})

const inviteStore = useFamilyGroupInviteStore()
const familyGroupStore = useFamilyGroupStore()
const dataGroupStore = useDataGroupStore()
const personStore = usePersonStore()
const userStore = useUserStore()
const manageStore = useManageStore()
const contactStore = useContactStore()
const tokenManager = useTokenManager()

const inviteRef = ref<FamilyGroupInvite>()
const recipientEmailRef = ref<string>()
const recipientInputRef = ref<InstanceType<typeof FamilyGroupInviteRecipient>>()
const isRecruiterRef = ref(false)
const addMessageRef = ref(false)
const busyRef = ref(false)
const modalRef = ref<HTMLElement>()

const permissionsRef = computed(() => isNewRef.value ? ItemPermissions.Owner : 
  inviteStore.getAsyncPermissions(inviteRef.value?.id, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const senderUserRef = computed(() => userStore.getAsyncUser(inviteRef.value?.senderUserId)?.data) // loaded by parent
const recipientProfileRef = computed(() => {
  const profileGroup = dataGroupStore.getAsyncGroup(inviteRef.value?.profileDataGroupId)?.data // loaded by parent
  return personStore.getAsyncPerson(profileGroup?.startItemId, LoadMode.EnsureLoaded)?.data
})
const recipientUserRef = computed(() => {
  const profileGroup = dataGroupStore.getAsyncGroup(inviteRef.value?.profileDataGroupId)?.data // loaded by parent
  const profileOwner = userStore.getAsyncUser(profileGroup?.ownerId)?.data // loaded by parent (if exists)
  return (profileGroup?.id == profileOwner?.profileDataGroupId) ? profileOwner : undefined
})

const isNewRef = computed(() => !inviteRef.value?.id)
const isForSelfRef = computed(() => inviteRef.value?.recipientUserId == tokenManager.current.userId)
const isAcceptedRef = computed(() => inviteRef.value?.result === InvitationResult.Accepted)

const showInviteProfileRef = computed(() => inviteRef.value?.profileDataGroupId && inviteRef.value?.profileDataGroupId != recipientUserRef.value?.profileDataGroupId)

const familyGroupPermissionsRef = computed(() => 
  familyGroupStore.getAsyncPermissions(inviteRef.value?.familyGroupId, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const canSetRecruiterRef = computed(() => isNewRef.value && (familyGroupPermissionsRef.value & ItemPermissions.Owner))

const showSendRef = computed(() => isNewRef.value)
const isValidRecipientRef = computed(() => !!recipientUserRef.value || !!recipientInputRef.value?.validate())
const enableSendRef = computed(() => showSendRef.value && !busyRef.value && isValidRecipientRef.value)

const MinResendInterval = { days: 3 }
const nextResendDateRef = computed(() => inviteRef.value?.lastSentDate?.plus(MinResendInterval))
const showResendRef = computed(() => inviteRef.value && !isNewRef.value && !busyRef.value && inviteRef.value.isPending &&
  inviteRef.value.senderUserId == tokenManager.current.userId)
const enableResendRef = computed(() => showResendRef.value && DateTime.utc() > nextResendDateRef.value! && !busyRef.value)
const showResentAlertRef = ref(false)

const showCancelRef = computed(() => !isNewRef.value && inviteRef.value?.result == InvitationResult.Pending && 
  (permissionsRef.value & ItemPermissions.Owner) > 0)
const enableCancelRef = computed(() => showCancelRef.value && !busyRef.value)

const canAcceptRef = computed(() => inviteRef.value && isForSelfRef.value && inviteRef.value.result == InvitationResult.Pending)

async function openNew(familyGroupId: string, profilePersonId: string) {
  const inv = new FamilyGroupInvite()
  inv.familyGroupId = familyGroupId
  inv.senderUserId = tokenManager.current.userId
  inv.profileDataGroupId = CompositeId.getGroupId(profilePersonId)
  inviteRef.value = inv
  recipientEmailRef.value = manageStore.inviteAddresses.get(profilePersonId!)
  isRecruiterRef.value = false

  resetInteractiveState()

  // HACK: Not sure why getInstance isn't sufficient here, but sometimes it returns null because apparently the BS Modal
  // hasn't been initialized yet. Calling getOrCreateInstance seems to fix it.
  BsModal.getOrCreateInstance(modalRef.value!).show()

  await nextTick()
  recipientInputRef.value?.focus()  
}

function openEdit(inviteId: string) {
  inviteRef.value = inviteStore.getAsyncInvite(inviteId)?.data // loaded by parent
  recipientEmailRef.value = inviteRef.value?.recipientEmail
  isRecruiterRef.value = inviteRef.value?.recipientRole == GroupMemberRole.Recruiter

  resetInteractiveState()
  
  // HACK: Not sure why getInstance isn't sufficient here, but sometimes it returns null because apparently the BS Modal
  // hasn't been initialized yet. Calling getOrCreateInstance seems to fix it.
  BsModal.getOrCreateInstance(modalRef.value!).show()
}

function resetInteractiveState() {
  busyRef.value = false
  showResentAlertRef.value = false
}

function close() {
  BsModal.getInstance(modalRef.value!)?.hide()
}

async function sendInvite() {
  const inv = inviteRef.value!
  inv.recipientUserId = recipientUserRef.value?.id
  inv.recipientEmail = manageStore.inviteAddresses.get(recipientProfileRef.value!.id!)
  const name = new PersonName(recipientProfileRef.value!.displayProperties.name)
  inv.recipientFirstName = name.given
  inv.recipientLastName = name.surname
  inv.recipientRole = isRecruiterRef.value ? GroupMemberRole.Recruiter : GroupMemberRole.Member
  inv.profileDisplayName = recipientProfileRef.value!.displayName
  inv.profileGender = recipientProfileRef.value!.displayProperties.gender as string as UserGender // same values

  busyRef.value = true
  await inviteStore.sendAsync(inv)
  close()
}

async function cancelInvite() {
  busyRef.value = true
  await inviteStore.cancelAsync(inviteRef.value!.id!)
  close()
}

async function resendInvite(e: Event) {
  busyRef.value = true
  const inviteId = inviteRef.value!.id! // capture this before the await

  await AsyncUtil.delay(500)
  await inviteStore.resendAsync(inviteRef.value!.id!, undefined, inviteRef.value!.senderMessage)

  busyRef.value = false
  showResentAlertRef.value = true
  // don't close
}

async function acceptInvite(e: Event) {
  busyRef.value = true
  await inviteStore.acceptAsync(inviteRef.value!.id!)
  close()
}
</script>